import { type State, useAuthStore } from '@/store/auth';
import { apiBasePath } from '@/utils/apiBasePath';
import { AuthApi, Configuration } from '@teemill/auth';
import { useLocalStorage, whenever } from '@vueuse/core';
import { useTokenCache } from './tokenCache';
import { onWatcherCleanup, type Ref } from 'vue';

const client = new AuthApi(
  new Configuration({
    basePath: apiBasePath(),
    baseOptions: {
      headers: {
        'User-Agent': null,
      },
    },
  }),
);

const useAuthorizationCode = () =>
  useLocalStorage<string | undefined>('authorizationCode', undefined);

const tokenCache = useTokenCache('authToken', async () => {
  const store = useAuthStore();

  if (store.accessToken === undefined) {
    return undefined;
  }

  return await store.accessToken();
});

export function useAuthToken(options?: { autoRefresh?: false }): Promise<string>;
export function useAuthToken(options: { autoRefresh: true }): Ref<string | undefined>;
export function useAuthToken(options?: { autoRefresh?: boolean }) {
  if (!options?.autoRefresh) {
    return tokenCache.get();
  }

  const store = useAuthStore();
  const token = tokenCache.auto();

  /**
   * Force a fresh token if the authenticated user changes.
   */
  whenever(
    () => store.isAuthenticated,
    () => {
      let active = true;

      token.value = undefined;
      tokenCache.get().then((newToken) => {
        if (active) {
          token.value = newToken;
        }
      });

      onWatcherCleanup(() => {
        active = false;
      });
    },
  );

  return token;
}

export const useAuth = () => {
  const store = useAuthStore();

  return {
    isAuthenticated: () => store.isAuthenticated,
    isAuthenticatedAs: (type: State['type']) => store.isAuthenticatedAs(type),
    authenticateAs: (type: State['type'], accessToken?: State['accessToken']) => {
      store.update(type, accessToken);
    },
    unAuthenticate: () => {
      useAuthorizationCode().value = undefined;
      store.update(undefined);
    },
  };
};

export const auth = {
  async install() {
    const auth = useAuth();
    const code = useAuthorizationCode().value;

    if (code !== undefined) {
      auth.authenticateAs('grant', async () => {
        const {
          data: { token },
        } = await client.token({
          tokenRequest: {
            type: 'authorization_code',
            code,
          },
        });

        return token;
      });
    }
  },
};
